import React, { memo, useRef, useEffect, useState } from "react";

// Type
import { TableType } from "../type";

// Lib
import { sanitize } from "dompurify";
import { useWindowSize } from 'lib/hooks';

// Classnames
import clsx from "clsx";

// components
import Img from "components/Img";
import NoData from "components/NoData";
import UntilTable from "components/UntilTable";

// Types
interface ContainerHeightType {
  height: string;
  overflow: string;
}

// Constants
const ITEM_GAP = 10;
const RECALC_DELAY = 50;
const COLLAPSE_ITEMS = 3;
const MOBILE_BREAKPOINT = 560;

const initStateContainerHeight: ContainerHeightType = {
  height: "auto",
  overflow: "auto",
};

export const Table: React.FC<TableType> = memo((props) => {
  const { id, data = [], count, headerTitle } = props;

  const refList = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobileScreen = useWindowSize().width <= MOBILE_BREAKPOINT;

  const [isExpanded, setIsExpanded] = useState(false);
  const [containerHeight, setContainerHeight] = useState<ContainerHeightType>(initStateContainerHeight);

  const calculateHeights = () => {
    const container = containerRef.current;
    if (!container) return;

    const children = Array.from(container.children) as HTMLElement[];
    const fullHeight = container.scrollHeight;

    const collapsedHeight = children
      .slice(0, COLLAPSE_ITEMS)
      .reduce((acc, el) => acc + el.getBoundingClientRect().height, 0) + 
      (COLLAPSE_ITEMS - 1) * ITEM_GAP;

    setContainerHeight({
      overflow: isMobileScreen ? "hidden" : "auto",
      height: isMobileScreen ? (isExpanded ? `${fullHeight}px` : `${collapsedHeight}px`) : "auto",
    });
  };

  useEffect(() => {
    const timer = setTimeout(calculateHeights, RECALC_DELAY);
    return () => clearTimeout(timer);
  }, [data, isExpanded, isMobileScreen]);

  const configHeader = [
    {
      label: "libraries",
      value: data.length
    },
    {
      value: count,
      label: "unique<br/> compounds"
    }
  ];

  const cnWrapper = clsx("table-container", id && `table-container--${id}`);

  return (
    <div id={id} ref={refList} className="screen-libraries__table relative">
      <Img
        isSvg
        name="bigFolderSvg"
        className="screen-libraries__svg"
      />
      <div className="screen-libraries__wrapper-content">
        <div className="screen-libraries__table-header flex flex-ai-center flex-jc-between">
          <h4 dangerouslySetInnerHTML={{ __html: sanitize(headerTitle ?? "") }} />

          <div className="screen-libraries__wrapper-header flex">
            {configHeader.map((item, i) => (
              <div key={`${item.label}_${i}`} className="screen-libraries__table-info">
                <span className="screen-libraries__table-count">{item.value}</span>
                <p className="screen-libraries__table-text" dangerouslySetInnerHTML={{ __html: item.label ?? "" }} />
              </div>
            ))}
          </div>
        </div>

        <div className="screen-libraries__table-small-header flex flex-ai-center flex-jc-between">
          <span className="screen-libraries__table-small-header-name">Name</span>
          <div className="screen-libraries__table-small-right flex flex-ai-center flex-jc-between">
            <span>Description</span>
            <span>Number of compounds</span>
          </div>
        </div>

        <div
          ref={containerRef}
          className={cnWrapper}
          style={{
            ...containerHeight,
            transition: "height 300ms ease"
          }}
        >
          {data.length > 0 
            ? data.map((x, i) => <UntilTable {...x} key={i} isRedirect />)
            : <NoData text="No data available" />}
        </div>

        {data.length >= 2 && (
          <div
            onClick={() => setIsExpanded(prev => !prev)}
            className="screen-libraries__text-show"
          >
            {isExpanded ? "Hide all" : "Show all"}
          </div>
        )}
      </div>
    </div>
  );
});
